/**
 * Sticky Buy Button for Mobile
 * Makes the buy button stick to the bottom of the screen when the product-details div is no longer visible
 */

class StickyBuyButton {
  constructor() {
    this.originalButton = null;
    this.stickyButton = null;
    this.stickyContainer = null;
    this.observer = null;
    this.productInfoObserver = null;
    this.isMobile = window.innerWidth <= 768;
    
    if (!this.isMobile) return;
    
    this.init();
  }

  init() {
    // Find the original buy button
    this.originalButton = document.querySelector('.add-to-cart-button');
    
    if (!this.originalButton) {
      console.warn('Sticky Buy Button: Original button not found');
      return;
    }

    // Find the product-details div to observe
    this.productDetails = document.querySelector('.product-details');
    
    if (!this.productDetails) {
      console.warn('Sticky Buy Button: Product details div not found');
      return;
    }

    // Find the product-information div to observe
    this.productInformation = document.querySelector('.product-information');

    // Check if sticky button already exists (prevent duplicates)
    if (document.querySelector('.sticky-buy-button-container')) {
      return;
    }

    this.createStickyButton();
    this.setupIntersectionObserver();
    this.setupEventListeners();
  }

  createStickyButton() {
    // Create sticky container
    this.stickyContainer = document.createElement('div');
    this.stickyContainer.className = 'sticky-buy-button-container';
    this.stickyContainer.setAttribute('aria-hidden', 'true');
    
    // Clone the original button and its parent form
    const productForm = this.originalButton.closest('form');
    const productFormComponent = this.originalButton.closest('product-form-component');
    
    if (!productForm || !productFormComponent) {
      console.warn('Sticky Buy Button: Could not find product form');
      return;
    }

    // Clone the entire form structure needed
    const clonedForm = productForm.cloneNode(true);
    const clonedComponent = productFormComponent.cloneNode(false);
    
    // Clean up the cloned form - keep only essential elements
    const clonedButton = clonedForm.querySelector('.add-to-cart-button');
    const hiddenInputs = clonedForm.querySelectorAll('input[type="hidden"]');
    const quantitySelector = clonedForm.querySelector('.quantity-selector');
    
    // Remove everything except the button and essential inputs
    const productFormButtons = clonedForm.querySelector('.product-form-buttons');
    if (productFormButtons) {
      // Clear and add only what we need
      productFormButtons.innerHTML = '';
      
      // Add hidden inputs
      hiddenInputs.forEach(input => {
        productFormButtons.appendChild(input.cloneNode(true));
      });
      
      // Add the button
      if (clonedButton) {
        clonedButton.id = clonedButton.id + '-sticky';
        productFormButtons.appendChild(clonedButton);
      }
    }

    // Set up the component structure
    clonedComponent.appendChild(clonedForm);
    this.stickyContainer.appendChild(clonedComponent);
    
    // Add to page
    document.body.appendChild(this.stickyContainer);
    
    this.stickyButton = this.stickyContainer.querySelector('.add-to-cart-button');
    
    // Set up validation for the sticky button
    this.setupStickyButtonValidation();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    // Track visibility state of both elements
    this.visibilityState = {
      productDetails: false,
      productInformation: false
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.target === this.productDetails) {
          this.visibilityState.productDetails = entry.isIntersecting;
        }
        this.updateStickyVisibility();
      });
    }, options);

    // Observe the product-details div
    this.observer.observe(this.productDetails);

    // Set up observer for product-information if it exists
    if (this.productInformation) {
      this.productInfoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.target === this.productInformation) {
            this.visibilityState.productInformation = entry.isIntersecting;
          }
          this.updateStickyVisibility();
        });
      }, options);

      this.productInfoObserver.observe(this.productInformation);
    }
  }

  updateStickyVisibility() {
    // Hide sticky if either product-details OR product-information is visible
    if (this.visibilityState.productDetails || this.visibilityState.productInformation) {
      this.hideStickyButton();
    } else {
      this.showStickyButton();
    }
  }

  showStickyButton() {
    if (this.stickyContainer) {
      this.stickyContainer.classList.add('visible');
      // Fallback for browsers that don't support :has()
      document.body.classList.add('sticky-button-active');
    }
  }

  hideStickyButton() {
    if (this.stickyContainer) {
      this.stickyContainer.classList.remove('visible');
      // Fallback for browsers that don't support :has()
      document.body.classList.remove('sticky-button-active');
    }
  }

  setupEventListeners() {
    // Handle resize to enable/disable on mobile (debounced)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;
        
        if (wasMobile && !this.isMobile) {
          // Switched from mobile to desktop
          this.destroy();
        } else if (!wasMobile && this.isMobile) {
          // Switched from desktop to mobile
          this.init();
        }
      }, 250);
    });

    // Sync button states
    if (this.originalButton && this.stickyButton) {
      // Listen for changes on original button
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const attributeName = mutation.attributeName;
            if (attributeName === 'disabled' || attributeName === 'aria-disabled') {
              const value = this.originalButton.getAttribute(attributeName);
              if (value) {
                this.stickyButton.setAttribute(attributeName, value);
              } else {
                this.stickyButton.removeAttribute(attributeName);
              }
            }
          } else if (mutation.type === 'childList') {
            // Sync text content changes
            const originalText = this.originalButton.querySelector('.add-to-cart-text__content');
            const stickyText = this.stickyButton.querySelector('.add-to-cart-text__content');
            if (originalText && stickyText) {
              stickyText.innerHTML = originalText.innerHTML;
            }
          }
        });
      });

      observer.observe(this.originalButton, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.productInfoObserver) {
      this.productInfoObserver.disconnect();
      this.productInfoObserver = null;
    }
    if (this.stickyContainer) {
      this.stickyContainer.remove();
      this.stickyContainer = null;
    }
    // Clean up body class
    document.body.classList.remove('sticky-button-active');
    
    // Reset references
    this.originalButton = null;
    this.stickyButton = null;
    this.productDetails = null;
    this.productInformation = null;
    this.visibilityState = null;
  }

  setupStickyButtonValidation() {
    if (!this.stickyButton) {
      console.log('❌ No sticky button found for validation setup');
      return;
    }

    const stickyForm = this.stickyButton.closest('form');
    if (!stickyForm) {
      console.log('❌ No sticky form found for validation setup');
      return;
    }

    // Check if this is a plush product that needs validation
    const isPlushProduct = this.stickyButton.classList.contains('plush-product-button') || 
                          this.stickyButton.hasAttribute('data-is-plush');

    console.log('🔧 Setting up sticky button validation');
    console.log('🧸 Is plush product:', isPlushProduct);
    console.log('🏷️ Button classes:', this.stickyButton.className);
    console.log('🏷️ Button attributes:', this.stickyButton.attributes);

    // Set up validation for ALL products (not just plush) to handle debugging
    // The actual plush validation will be checked in validateAndSubmit()

    // Hijack form submission for validation
    stickyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      
      console.log('📋 Sticky form submitted');
      this.validateAndSubmit();
    });

    // Also hijack button click
    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      
      console.log('🖱️ Sticky button clicked');
      
      // Trigger validation directly on click
      this.validateAndSubmit();
    });
  }

  validateAndSubmit() {
    console.log('🔍 Starting validation for sticky button');
    
    // Check if ProductCustomization is available and this is a plush product
    const isPlushProduct = this.stickyButton.classList.contains('plush-product-button') || 
                          this.stickyButton.hasAttribute('data-is-plush');
    
    console.log('🧸 Is plush product:', isPlushProduct);
    console.log('🛠️ ProductCustomization available:', !!window.ProductCustomization);
    
    if (isPlushProduct) {
      // Use the existing ProductCustomization validation if available
      if (window.ProductCustomization && typeof window.ProductCustomization.validate === 'function') {
        console.log('✅ Using ProductCustomization.validate()');
        const errors = window.ProductCustomization.validate();
        console.log('🔍 Validation errors:', errors);
        
        if (errors.length > 0) {
          console.log('❌ Sticky button: Validation échouée', errors);
          this.handleValidationErrors(errors);
          return false;
        }
        
        // If validation passes, use the existing customization logic
        console.log('✅ Validation passed, adding to cart with customization');
        if (typeof window.ProductCustomization.addToCartWithCustomization === 'function') {
          window.ProductCustomization.addToCartWithCustomization();
        } else {
          // Fallback to original form submission
          this.submitOriginalForm();
        }
      } else {
        // Fallback validation for message
        console.log('⚠️ Using fallback validation');
        const messageTextarea = document.getElementById('personal-message');
        console.log('📝 Message textarea found:', !!messageTextarea);
        console.log('📝 Message value:', messageTextarea ? messageTextarea.value : 'N/A');
        
        if (messageTextarea && (!messageTextarea.value || messageTextarea.value.trim() === '')) {
          console.log('❌ Sticky button: Message personnalisé manquant');
          this.handleValidationErrors(['message_required']);
          return false;
        }
        
        // If validation passes, submit original form
        console.log('✅ Fallback validation passed');
        this.submitOriginalForm();
      }
    } else {
      // Not a plush product, submit normally
      console.log('📦 Not a plush product, submitting normally');
      this.submitOriginalForm();
    }
  }

  handleValidationErrors(errors) {
    console.log('🚨 Handling validation errors:', errors);
    
    // Show validation errors using existing system if available
    if (window.ProductCustomization && typeof window.ProductCustomization.showValidationErrors === 'function') {
      console.log('✅ Using ProductCustomization.showValidationErrors()');
      window.ProductCustomization.showValidationErrors(errors);
    } else {
      // Fallback error display
      console.log('⚠️ Using fallback error display');
      this.showFallbackValidationError(errors);
    }
    
    // Scroll to message form wrapper if message is required
    if (errors.includes('message_required')) {
      console.log('📍 Scrolling to message form');
      this.scrollToMessageForm();
    }
  }

  showFallbackValidationError(errors) {
    if (errors.includes('message_required')) {
      const messageForm = document.querySelector('.personal-message-form');
      if (messageForm) {
        // Remove existing error
        const existingError = messageForm.querySelector('.validation-error');
        if (existingError) {
          existingError.remove();
        }
        
        // Create new error message
        const errorDiv = document.createElement('div');
        errorDiv.className = 'validation-error';
        errorDiv.style.cssText = 'color: #e74c3c; font-size: 12px; margin-top: 4px; font-weight: 500;';
        errorDiv.textContent = 'Veuillez ajouter un message pour votre carte personnalisée.';
        messageForm.appendChild(errorDiv);
        
        console.log('✅ Fallback error message displayed');
      } else {
        console.log('❌ Could not find .personal-message-form for error display');
      }
    }
  }

  scrollToMessageForm() {
    const messageFormWrapper = document.querySelector('.message-form-wrapper');
    if (messageFormWrapper) {
      console.log('📍 Found .message-form-wrapper, scrolling...');
      
      // Hide sticky button temporarily while scrolling
      this.hideStickyButton();
      
      // Smooth scroll to the message form
      messageFormWrapper.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Focus the textarea after scrolling
      setTimeout(() => {
        const textarea = document.getElementById('personal-message');
        if (textarea) {
          console.log('🎯 Focusing textarea');
          textarea.focus();
        } else {
          console.log('❌ Could not find #personal-message textarea');
        }
      }, 500);
      
      // Re-show sticky button after a delay (if still needed)
      setTimeout(() => {
        console.log('🔄 Checking if sticky should reappear');
        // Check if neither product-details nor product-information are visible
        let shouldShow = true;
        
        if (this.productDetails) {
          const detailsRect = this.productDetails.getBoundingClientRect();
          const detailsVisible = detailsRect.top < window.innerHeight && detailsRect.bottom > 0;
          console.log('👁️ Product details visible:', detailsVisible);
          if (detailsVisible) shouldShow = false;
        }
        
        if (this.productInformation && shouldShow) {
          const infoRect = this.productInformation.getBoundingClientRect();
          const infoVisible = infoRect.top < window.innerHeight && infoRect.bottom > 0;
          console.log('👁️ Product information visible:', infoVisible);
          if (infoVisible) shouldShow = false;
        }
        
        if (shouldShow) {
          console.log('↗️ Re-showing sticky button');
          this.showStickyButton();
        }
      }, 1000);
    } else {
      console.log('❌ Could not find .message-form-wrapper for scrolling');
      
      // Try alternative selectors
      const personalMessageForm = document.querySelector('.personal-message-form');
      const messageTextarea = document.getElementById('personal-message');
      
      if (personalMessageForm) {
        console.log('📍 Found .personal-message-form as fallback, scrolling...');
        this.hideStickyButton();
        personalMessageForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (messageTextarea) {
            messageTextarea.focus();
          }
        }, 500);
      } else if (messageTextarea) {
        console.log('📍 Found #personal-message as fallback, scrolling...');
        this.hideStickyButton();
        messageTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          messageTextarea.focus();
        }, 500);
      } else {
        console.log('❌ No suitable element found for scrolling');
      }
    }
  }

  submitOriginalForm() {
    // Find and submit the original form
    const originalForm = this.originalButton.closest('form');
    if (originalForm) {
      console.log('✅ Soumission du formulaire original');
      
      // Create a new submit event to bypass any hijacking
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true
      });
      
      // Temporarily remove hijacking to allow normal submission
      originalForm.dispatchEvent(submitEvent);
    }
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyBuyButton();
  });
} else {
  new StickyBuyButton();
}

// Also initialize on page transitions (for SPA-like behavior)
document.addEventListener('shopify:section:load', () => {
  new StickyBuyButton();
});
